import { warn, error } from '@ali/re-utils';
import Action from './Action';

export default class EventsExecutor {
  constructor(engine, xprops, defaultMutators) {
    this.engine = engine;
    this.xprops = xprops;
    this.defaultMutators = defaultMutators || {
      onChange: ({ state }, data) => {
        if (state.get('fieldData')) {
          state.merge('fieldData', {
            ...data,
            dataType: 'CHANGED',
          });
        }
      },
    };
  }

  createContext(args, previousActionData, params) {
    const { context, proxiedContext } = this.engine.createAPIContext(this.xprops, params);
    context.setParams({
      args,
      previousActionData,
      ...params,
    });
    return { context, proxiedContext };
  }

  changeCapture() {
    // const root = this.xprops.getState().getRoot();
    // const store = this.xprops.getStore();
    // const release = [store.changeCapture()];
    // if ((root !== store) && root) {
    //   release.push(root.changeCapture());
    // }
    // return (() => release.forEach(r => r()));
    return () => {};
  }

  execQueue(queue, args) {
    return new Promise((resolve, reject) => {
      const execResult = [];
      if (!queue) return;

      queue = queue.slice(0);

      let captureEnd = null;

      const done = (withError) => {
        if (captureEnd) {
          captureEnd();
          captureEnd = null;
        }
        if (withError) {
          reject(withError);
        } else {
          resolve(execResult);
        }
      };

      const fail = (e) => {
        if (e instanceof Error) {
          done(e);
          error(e);
        } else {
          done();
        }
      };

      const next = (data) => {
        const item = queue.shift();
        if (!item) {
          return done();
        }
        /* eslint-disable react/no-this-in-sfc */
        if (!captureEnd) {
          captureEnd = this.changeCapture();
        }
        let res;
        try {
          const { proxiedContext, context } = this.createContext(args, data, item.params);
          /* eslint-enable react/no-this-in-sfc */
          let actionArgs = args;
          if (context.isContextInAction) {
            actionArgs = [context, ...args];
          }
          res = item.action.invoke(proxiedContext, actionArgs);
          execResult.push(res);
        } catch (e) {
          execResult.push(undefined);
          return fail(e.message);
        }

        if (res === false) {
          return done();
        }

        if (res && res.then) {
          if (captureEnd) {
            captureEnd();
            captureEnd = null;
          }
          // todo: think about timeout
          res.then(next, fail).catch(fail);
        } else if (res instanceof Error) {
          fail(res);
        } else {
          next(res);
        }
        return null;
      };

      next();
    });
  }

  createEntry(eventType, events) {
    const mutator = this.defaultMutators[eventType];
    const eventsQueue = this.prepareQueue(events);
    return (...args) => {
      // todo: think about continuous trigger
      if (mutator) {
        const { proxiedContext, context } = this.createContext(args);
        if (mutator.apply(proxiedContext, [context, ...args]) === false) {
          return false;
        }
      }
      return this.execQueue(eventsQueue, args);
    };
  }

  parseEvent(event) {
    if (!event) return null;

    const type = typeof event;
    if (type === 'function' || type === 'string') {
      return {
        action: new Action({ func: event }),
      };
    }
    if (type === 'object') {
      let action;
      if (event.compiled != null) {
        action = new Action({ func: event.compiled });
      } else if (event.func) {
        action = new Action({ func: event.func });
      } else if (event.id) {
        action = this.engine.getAction(event.id);
        if (!action) {
          warn('cannot find action from ', event);
          return null;
        }
      } else {
        warn('invalid event setting', event);
        return null;
      }

      return {
        action,
        params: event.params,
      };
    }

    return null;
  }

  prepareQueue(events) {
    if (!events) return null;
    if (!Array.isArray(events)) {
      const item = this.parseEvent(events);
      return item ? [item] : null;
    }
    return events.map(this.parseEvent, this).filter(item => item !== null);
  }

  parse(events) {
    const eventProps = {};
    if (events) {
      Object.keys(events).forEach((eventType) => {
        eventProps[eventType] = this.createEntry(eventType, events[eventType]);
      });
    }
    Object.keys(this.defaultMutators).forEach((eventType) => {
      if (!eventProps.hasOwnProperty(eventType)) {
        eventProps[eventType] = this.createEntry(eventType);
      }
    });
    return eventProps;
  }
}
